import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function ProblemTypesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_service_settings");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", icon: "Wrench" });

  const load = async () => {
    try {
      const { data } = await api.get("/service/problem-types");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", icon: "Wrench" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, icon: it.icon || "Wrench" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nume necesar");
    try {
      if (editing) await api.patch(`/service/problem-types/${editing.id}`, form);
      else await api.post("/service/problem-types", { ...form, order: items.length });
      toast.success(editing ? "Tip actualizat" : "Tip creat");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/service/problem-types/${it.id}`, { active: !it.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (it) => {
    if (!confirm(`Ștergi tipul "${it.name}"?`)) return;
    try {
      await api.delete(`/service/problem-types/${it.id}`);
      toast.success("Tip șters");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="problem-types-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări · Service</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Tipuri de probleme</h1>
          <p className="text-muted-foreground text-sm mt-1">Configurează lista din care clienții aleg natura defectului.</p>
        </div>
        {canManage && <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1.5" /> Tip nou</Button>}
      </div>

      <Card className="p-4 bg-card border-border">
        {loading ? (
          <div className="text-sm text-muted-foreground p-4">Se încarcă…</div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 p-3 bg-secondary/30 border border-border rounded-md" data-testid={`problem-type-${it.name}`}>
                <span className="text-sm font-medium flex-1">{it.name}</span>
                <Badge variant="outline" className={it.active ? "text-emerald-500 border-emerald-500/30" : "text-muted-foreground"}>
                  {it.active ? "Activ" : "Inactiv"}
                </Badge>
                {canManage && (
                  <>
                    <Switch checked={it.active} onCheckedChange={() => toggleActive(it)} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => del(it)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editează tip" : "Tip nou"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nume</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Problemă baterie" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={save}>{editing ? "Salvează" : "Creează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
